import { GlobalHeaderState, HTTP_METHODS, makeDownstreamApiCall } from "@swiggy-private/checkout-core";
import { CREDIT_TYPE, GetAllCardsResponse, GetCardDetailsResponse } from "@payment-client/interfaces/CardApi";
import { isBusinessLineSwiggyMoney, isOriginalBusinessLineSwiggyMoney } from "@payment-client/helpers/Misc";
import { isEmpty } from "@swiggy-private/js-utils";
import { Storage, format } from "@swiggy-private/core-ui";
import { GIFT_DETAILS_STORAGE_KEY } from "@payment-client/constants/StorageKeys";
import { ICardOffer } from "@swiggy-private/payment-ui";
import { ApiResponse } from "@payment-client/interfaces/Api";
import { getMarketplaceBusinessLine } from "@payment-client/utils/Headers";
import { MARKETPLACE_BUSINESS_LINE, MARKETPLACE_ID } from "@swiggy-private/payment-types";
export interface GetSodexoBalanceResponse {
    cardToken: string;
    cardReference: string;
    cardBalance: number | string;
}

interface UpdateCardDetailPayload {
    cardRef: string;
    customerId: string;
    nameOnCard?: string;
    nickName: string;
}

export const ENDPOINT = {
    GET_SODEXO_BALANCE: "/api/v3/get-sodexo-balance",
    GET_ALL_CARDS: "/api/v3/get-all-cards",
    GET_CARD_DETAILS: "/api/v3/get-card-details/{0}",
    GET_CARD_OFFER: "/api/v3/get-card-offer",
    DELETE_CARD: "/api/v3/delete-card",
    UPDATE_CARD: "/api/v3/update-card",
};

export async function getSodexoBalancePublic(apiHost: string): Promise<GetSodexoBalanceResponse[]> {
    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: ENDPOINT.GET_SODEXO_BALANCE,
        options: {},
    });

    return response.data as GetSodexoBalanceResponse[];
}

export async function getAllCardsPublic(apiHost: string): Promise<GetAllCardsResponse> {
    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: ENDPOINT.GET_ALL_CARDS,
        options: {
            headers: {
                source: "account_page",
            },
        },
    });

    return response.data as GetAllCardsResponse;
}

export async function getCardDetailsPublic(
    apiHost: string,
    cardBin: string,
    selectedEMIBankCode?: string,
): Promise<GetCardDetailsResponse | null> {
    let workflow = "";
    const URI = format(ENDPOINT.GET_CARD_DETAILS, cardBin);

    if (isBusinessLineSwiggyMoney() || isOriginalBusinessLineSwiggyMoney()) {
        const giftDetails = Storage.getItem(GIFT_DETAILS_STORAGE_KEY, null);
        if (!isEmpty(giftDetails)) {
            workflow = CREDIT_TYPE.VOUCHER;
        } else {
            workflow = CREDIT_TYPE.SELFLOAD;
        }
    }

    // eslint-disable-next-line @typescript-eslint/naming-convention
    const searchParams: { workflow: string; selected_bank_code: string } = {
        workflow,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        selected_bank_code: selectedEMIBankCode || "",
    };

    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: URI,
        searchParams: searchParams,
    });

    return response.data as GetCardDetailsResponse | null;
}

/**
 * Maps marketplace business line to corresponding marketplace ID
 * Returns empty string if no mapping exists
 */
function getMarketplaceId(businessLine: string): string {
    const businessLineToIdMap: Record<string, string> = {
        [MARKETPLACE_BUSINESS_LINE.SWIGGY_MONEY]: MARKETPLACE_ID.SWIGGY_MONEY,
        [MARKETPLACE_BUSINESS_LINE.DASH]: MARKETPLACE_ID.DASH,
        [MARKETPLACE_BUSINESS_LINE.DINEOUT]: MARKETPLACE_ID.DINEOUT,
        [MARKETPLACE_BUSINESS_LINE.ASSURE]: MARKETPLACE_ID.ASSURE,
    };

    return businessLineToIdMap[businessLine] || "";
}

export async function getCardOfferPublic(
    apiHost: string,
    cardBin: string,
    withoutSplitPay: string,
): Promise<ICardOffer | null> {
    const headers = GlobalHeaderState.getHeaders();
    const marketplaceBusinessLine = getMarketplaceBusinessLine(headers);
    const marketplaceId = getMarketplaceId(marketplaceBusinessLine);

    const requestHeaders: Record<string, string> = {};
    if (marketplaceId) {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        requestHeaders["m_id"] = marketplaceId;
    }

    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: ENDPOINT.GET_CARD_OFFER,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        bodyParams: { card_bin: cardBin, without_split_pay: withoutSplitPay || false },
        options: {
            method: HTTP_METHODS.POST,
            headers: requestHeaders,
        },
    });

    return response.data as ICardOffer | null;
}

export async function deleteCardPublic(apiHost: string, cardReference: string): Promise<ApiResponse> {
    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: ENDPOINT.DELETE_CARD,
        // eslint-disable-next-line @typescript-eslint/naming-convention
        bodyParams: { card_reference: cardReference },
        options: {
            method: HTTP_METHODS.POST,
        },
    });

    return response as ApiResponse;
}

export async function updateCardPublic(apiHost: string, cardDetail: UpdateCardDetailPayload): Promise<ApiResponse> {
    const response = await makeDownstreamApiCall({
        apiHost,
        endpoint: ENDPOINT.UPDATE_CARD,
        bodyParams: {
            /* eslint-disable @typescript-eslint/naming-convention */
            card_ref: cardDetail.cardRef,
            customer_id: cardDetail.customerId,
            name_on_card: cardDetail.nameOnCard,
            nickname: cardDetail.nickName,
            /* eslint-enable @typescript-eslint/naming-convention */
        },
        options: {
            method: HTTP_METHODS.POST,
        },
    });

    return response as ApiResponse;
}
